import {LocaleConfig} from 'platform/locale';
import {Pattern, match} from 'ts-pattern';

import {always, replace} from 'ramda';

const TRAILING_SEPARATOR_REGEX = /[.,]$/;
const NO_DECIMAL_PLACES = 0;
const NO_SEPARATOR = '';

/**
 * @about
 * Normalizes the decimal separator typed at the end of the input value to the separator of the
 * current locale, so the user can start the decimal part with either `.` or `,`.
 *
 * A trailing separator which is the thousands separator of the locale is kept untouched, because
 * in that locale it is not a decimal separator.
 *
 * When the input allows no decimal places, the trailing separator is removed instead of replaced.
 *
 * @example
 * // cs locale (decimal separator is `,`)
 * normalizeDecimalSeparator('7.', numberLocale) // '7,'
 * normalizeDecimalSeparator('7.', numberLocale, 0) // '7'
 */
export const normalizeDecimalSeparator = (
  value: string | null,
  numberLocale: LocaleConfig['number'],
  decimalPlaces?: number
) => {
  const rawValue = value ?? NO_SEPARATOR;
  const typedSeparator = TRAILING_SEPARATOR_REGEX.exec(rawValue)?.[0];

  const localeSeparator = match(decimalPlaces)
    .with(NO_DECIMAL_PLACES, always(NO_SEPARATOR))
    .otherwise(always(numberLocale.decimalSeparator));

  return match(typedSeparator)
    .with(Pattern.nullish, always(value))
    .with(numberLocale.thousandsSeparator, always(value))
    .otherwise(always(replace(TRAILING_SEPARATOR_REGEX, localeSeparator, rawValue)));
};
